// What the latest publish added.
//
// A new export brings elements the tracker has never seen, and nothing
// on the board used to call them out: under a tracked category they
// were counted straight away as projected, under an untracked one they
// were invisible. This lists them - the newest commit minus the one
// before it, computed once per publish on the server - by category and
// a page at a time, with the same status controls as the tree, a bulk
// mark for all of them (or one category), a way to track the categories
// they fall in, and a switch that shows only them in the model.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Empty, Select, Spin, Switch, Table, Tag, Tooltip, message } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { getNewElementUids, getNewElements, setElementStatus } from "api/tracking/trackingElementsApi";
import BulkBar from "./BulkBar";
import { announceStatus, setStatusWithPreview } from "./statusBulk";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { dateForStatus } from "./stageHelpers";

const T = { line: "#EFEFEF", muted: "#8D8D8D", ink: "#23343E" };
const PAGE = 100;

export default function NewSincePublish({
  projectId,
  documentIds = [],          // linked-file prefixes, as the tree filters
  canEdit = false,
  canEditScope = false,
  statusDate = "",
  projectedDate = "",        // when a projection is EXPECTED - its own date
  onProjectedDateChange = null,
  statusByUid = null,        // live statuses from the page, laid over the rows
  onStatusChanged = null,    // (uids, status) after a mark, so the model recolours
  onTrackKeys = null,        // (keys) puts categories into the tracked scope
  onFocus = null,            // (uids | null) show only these in the model
  onHighlight = null,        // (uids) frame and flash these in the model
  focused = false,
  onAddToList = null,        // (uids) open the page's "new list" with these
  onUndo = null,             // the page's undo of the last status change
  undoBusy = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [bulkStatus, setBulkStatus] = useState("installed");
  const [busy, setBusy] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  // A CATEGORY CHIP IS THIS LIST'S PARENT ROW. Ticking one means every
  // element in it, including the pages nobody has scrolled to - the same
  // promise a tick on a category makes in the two trees (owner via the
  // leader, 2026-09-20: he should not have to learn which one he is
  // looking at). Resolved by the diff's OWN endpoint, not by tree-uids:
  // these rows are the publish difference, not a branch of the model.
  const [checkedCats, setCheckedCats] = useState([]);
  const [selUids, setSelUids] = useState([]);
  const [selCapped, setSelCapped] = useState(false);
  const docs = useMemo(() => documentIds.join(","), [documentIds]);

  // What is on screen answers a choice here: picking a category (or
  // all) frames and flashes those elements in the model, and ticking
  // rows does the same for the rows.
  const highlightCategory = useCallback(async (cat) => {
    if (!onHighlight || !projectId) return;
    try {
      const r = await getNewElementUids(projectId, { documentIds: docs, category: cat || undefined });
      onHighlight(r.uids || []);
    } catch (e) { /* the list still works */ }
  }, [onHighlight, projectId, docs]);

  // What the ticks stand for, in ids. One call for all the ticked
  // categories, debounced, so ticking three of them is one question.
  useEffect(() => {
    let alive = true;
    if (!projectId || (!checkedCats.length && !selectedRows.length)) { setSelUids([]); setSelCapped(false); return undefined; }
    const t = setTimeout(async () => {
      try {
        const own = new Set(selectedRows);
        let capped = false;
        if (checkedCats.length) {
          const r = await getNewElementUids(projectId, { documentIds: docs, categories: checkedCats.join(",") });
          (r.uids || []).forEach((u) => own.add(u));
          capped = !!r.capped;
        }
        if (!alive) return;
        setSelUids([...own]);
        setSelCapped(capped);
      } catch (e) { if (alive) { setSelUids([]); setSelCapped(false); } }
    }, 120);
    return () => { alive = false; clearTimeout(t); };
  }, [projectId, docs, checkedCats, selectedRows]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await getNewElements(projectId, { documentIds: docs, category: category || undefined, limit: PAGE, skip: (page - 1) * PAGE });
      setData(r);
    } catch (e) {
      message.error(e.message || "Could not read what the last publish added");
    } finally {
      setLoading(false);
    }
  }, [projectId, docs, category, page]);
  useEffect(() => { load(); }, [load]);

  const mark = async (target, status, dateOverride) => {
    setBusy(true);
    try {
      const date = dateOverride || dateForStatus(status, statusDate, projectedDate);
      const r = await setElementStatus(projectId, { ...target, status, date: date || undefined, documentIds: docs || undefined });
      announceStatus(projectId, r, status, () => { if (onStatusChanged) onStatusChanged(null, status); load(); });
      if (onStatusChanged) onStatusChanged(target.uniqueIds || null, status);
      await load();
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBusy(false);
    }
  };

  // A ticked selection is set through the preview / confirm / undo path,
  // like every other bulk change on this page. Sent as IDS because that is
  // what a diff set is - and, for the same reason, "Link to a schedule
  // activity" is NOT on this bar: a set the next publish redefines must
  // not be attached to something durable (the owner settled that himself
  // on 2026-09-20; "Create a set, then link the set" is two visible
  // steps instead of one invisible lie).
  const markSelection = async (status) => {
    if (!selUids.length) return;
    setBusy(true);
    try {
      const r = await setStatusWithPreview(projectId, {
        uniqueIds: selUids, status, documentIds: docs || undefined,
        date: dateForStatus(status, statusDate, projectedDate) || undefined,
      });
      if (!r) return;
      announceStatus(projectId, r, status, () => { if (onStatusChanged) onStatusChanged(null, status); load(); });
      if (onStatusChanged) onStatusChanged(selUids, status);
      await load();
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBusy(false);
    }
  };

  const focus = async (on) => {
    if (!onFocus) return;
    if (!on) { onFocus(null); return; }
    try {
      const r = await getNewElementUids(projectId, { documentIds: docs, category: category || undefined });
      onFocus(r.uids || []);
      if (r.capped) message.warning(`Showing the first ${r.count.toLocaleString()} new elements`);
    } catch (e) {
      message.error(e.message || "Could not read the new elements");
    }
  };

  const when = (d) => (d ? new Date(d).toLocaleString() : "");
  const count = data ? data.count : 0;

  if (data && !data.prevCommitId) {
    return (
      <Card size="small" title="New since last publish" bodyStyle={{ padding: 10 }}>
        <div style={{ fontSize: 12, color: T.muted }}>
          This is the first publish of the model{data.commitAt ? ` (${when(data.commitAt)})` : ""}; there is no earlier one to compare with.
        </div>
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={
        <span>
          New since last publish
          {data ? <Tag style={{ marginLeft: 8 }} color={count ? "blue" : "default"}>{count.toLocaleString()}{data.capped ? "+" : ""}</Tag> : null}
        </span>
      }
      bodyStyle={{ padding: 10 }}
      extra={data && data.prevCommitAt ? (
        <span style={{ fontSize: 11, color: T.muted }}>{when(data.prevCommitAt)} → {when(data.commitAt)}</span>
      ) : null}
    >
      {loading && !data ? <div style={{ textAlign: "center", padding: 16 }}><Spin /></div> : null}
      {data && !count ? <Empty description="The last publish added no elements" style={{ padding: 12 }} /> : null}
      {data && count ? (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            {/* The tick SELECTS the category (all of it, every page); the
                chip still FILTERS what is listed. Two different things,
                which is why the tick is its own box rather than the chip
                doing both. */}
            {canEdit && (data.byCategory || []).length ? (
              <Checkbox
                style={{ marginRight: 2 }}
                checked={!!(data.byCategory || []).length && checkedCats.length === (data.byCategory || []).length}
                indeterminate={checkedCats.length > 0 && checkedCats.length < (data.byCategory || []).length}
                onChange={(e) => setCheckedCats(e.target.checked ? (data.byCategory || []).map((c) => c.value) : [])}
              >
                <span style={{ fontSize: 12, color: T.muted }}>All</span>
              </Checkbox>
            ) : null}
            <Tag.CheckableTag checked={!category} onChange={() => { setCategory(""); setPage(1); setSelectedRows([]); highlightCategory(""); }}>All</Tag.CheckableTag>
            {(data.byCategory || []).map((c) => (
              <span key={c.value} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                {canEdit ? (
                  <Checkbox
                    checked={checkedCats.includes(c.value)}
                    onChange={(e) => setCheckedCats((prev) => (e.target.checked ? [...prev, c.value] : prev.filter((x) => x !== c.value)))}
                    aria-label={`Select every new ${c.category}`}
                  />
                ) : null}
                <Tag.CheckableTag checked={category === c.value} onChange={() => { const next = category === c.value ? "" : c.value; setCategory(next); setPage(1); setSelectedRows([]); highlightCategory(next); }}>
                  {c.category} · {c.count.toLocaleString()}
                </Tag.CheckableTag>
              </span>
            ))}
          </div>
          {(checkedCats.length || selectedRows.length) ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", marginBottom: 8, border: `1px solid ${T.line}`, borderRadius: 6, background: "#F6F9FF" }}>
              <BulkBar
                count={selUids.length || null}
                selections={checkedCats.length + (selectedRows.length ? 1 : 0)}
                countNote={selCapped ? `more than ${selUids.length.toLocaleString()} elements - showing the first of them` : ""}
                canEdit={canEdit}
                busy={busy}
                onStatus={(key) => { setBulkStatus(key); markSelection(key); }}
                isolated={!!focused}
                onIsolate={() => { if (!onFocus) return; onFocus(focused ? null : selUids); }}
                onAddToList={onAddToList ? () => onAddToList(selUids) : null}
                onUndo={onUndo}
                undoBusy={undoBusy}
                onClear={() => { setCheckedCats([]); setSelectedRows([]); }}
              />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            {canEdit && (
              <>
                <Select size="small" style={{ minWidth: 150 }} value={bulkStatus} onChange={setBulkStatus}
                  options={STATUS_ORDER.map((v) => ({
                    value: v,
                    label: (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: STATUS_COLOR[v] }} />
                        {STATUS_LABEL[v] || v}
                      </span>
                    ),
                  }))} />
                {bulkStatus === "projected" && (
                  <Tooltip title="When these are expected to be installed">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: T.muted }}>
                      <CalendarOutlined />
                      <input type="date" value={projectedDate} onChange={(e) => onProjectedDateChange && onProjectedDateChange(e.target.value)}
                        style={{ fontSize: 12, padding: "1px 4px", border: `1px solid ${T.line}`, borderRadius: 4 }} />
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={bulkStatus === "projected" ? `Expected ${projectedDate || "today"}` : (statusDate ? `As of ${statusDate}` : "As of today")}>
                  <Button size="small" type="primary" loading={busy}
                    onClick={() => mark(selectedRows.length ? { uniqueIds: selectedRows } : { newSince: 1, category: category || undefined }, bulkStatus)}>
                    Mark {selectedRows.length ? `${selectedRows.length} selected` : (category ? `new ${category}` : "all new")}
                  </Button>
                </Tooltip>
              </>
            )}
            {canEditScope && onTrackKeys && (
              <Button size="small" onClick={() => onTrackKeys((category ? [category] : (data.byCategory || []).map((c) => c.value)).filter(Boolean).map((c) => `category:${c}`))}>
                Track {category ? "this category" : "their categories"}
              </Button>
            )}
            {onFocus && (
              <Tooltip title="Show only the new elements in the model">
                <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <Switch size="small" checked={!!focused} onChange={focus} />
                  Only new in model
                </span>
              </Tooltip>
            )}
          </div>
          <Table
            size="small"
            rowKey="uniqueId"
            loading={loading}
            dataSource={data.rows || []}
            rowSelection={{
              selectedRowKeys: [...selectedRows, ...(data.rows || []).filter((r) => checkedCats.includes(r.category)).map((r) => r.uniqueId)],
              // A row whose category is ticked is ticked, and cannot be
              // unticked on its own: that would silently drop the rows on
              // the pages nobody has opened. Untick the category instead -
              // the same rule as the trees.
              getCheckboxProps: (r) => (checkedCats.includes(r.category)
                ? { disabled: true, title: `Included because every new ${r.category} is ticked` } : {}),
              onChange: (keys) => {
                const own = keys.filter((k) => !(data.rows || []).some((r) => r.uniqueId === k && checkedCats.includes(r.category)));
                setSelectedRows(own);
                if (onHighlight) onHighlight(own);
              },
            }}
            pagination={{ current: page, pageSize: PAGE, total: data.total || 0, showSizeChanger: false, size: "small", onChange: (p) => setPage(p) }}
            columns={[
              {
                title: "", dataIndex: "status", width: 34,
                render: (s, r) => {
                  const live = (statusByUid && statusByUid.get(r.uniqueId)) || s;
                  return <span title={STATUS_LABEL[live] || live} style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[live] || "#ccc" }} />;
                },
              },
              { title: "Category", dataIndex: "category", ellipsis: true },
              { title: "Family", dataIndex: "family", ellipsis: true },
              { title: "Type", dataIndex: "typeName", ellipsis: true },
              { title: "Name", dataIndex: "name", ellipsis: true },
              { title: "Level", dataIndex: "level", width: 90, ellipsis: true },
              ...(canEdit ? [{
                title: "Status", key: "set", width: 250,
                render: (_, r) => {
                  const live = (statusByUid && statusByUid.get(r.uniqueId)) || r.status;
                  return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "100%" }}>
                      <Select size="small" style={{ flex: 1, minWidth: 130 }} value={live}
                        onChange={(v) => mark({ uniqueIds: [r.uniqueId] }, v)}
                        options={STATUS_ORDER.map((v) => ({
                          value: v,
                          label: (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <span style={{ width: 9, height: 9, borderRadius: 2, background: STATUS_COLOR[v] }} />
                              {STATUS_LABEL[v] || v}
                            </span>
                          ),
                        }))} />
                      {live === "projected" && (
                        <Tooltip title="When this one is expected - change the date to re-plan it">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: T.muted }}>
                            <CalendarOutlined />
                            <input type="date" defaultValue={r.statusDate ? String(r.statusDate).slice(0, 10) : projectedDate}
                              onChange={(e) => { if (e.target.value) mark({ uniqueIds: [r.uniqueId] }, "projected", e.target.value); }}
                              style={{ fontSize: 11, padding: "0 2px", border: `1px solid ${T.line}`, borderRadius: 4, width: 118 }} />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  );
                },
              }] : []),
            ]}
          />
        </>
      ) : null}
    </Card>
  );
}
